import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { useLang } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Rsvp = () => {
  const { c } = useLang();
  const r = c.rsvp;
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    attending: "yes",
    guest_count: 1,
    events: ["wedding"],
    meal: "",
    dietary: "",
    song_request: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [hasGuests, setHasGuests] = useState(false);
  const [step, setStep] = useState("lookup");
  const [party, setParty] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    axios.get(`${API}/guests/status`).then((res) => setHasGuests(res.data.has_guests)).catch(() => {});
  }, []);

  const search = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/guests/search`, { params: { q } });
      setResults(res.data);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const pickParty = (p) => {
    setParty(p);
    setAttendees(p.members.map((m) => ({ name: m, attending: true, meal: "" })));
    setForm((f) => ({ ...f, full_name: p.members[0] || p.party_name }));
    setStep("form");
  };

  const addGuestRow = () =>
    setAttendees((arr) => [...arr, { name: "", attending: true, meal: "", editable: true }]);

  const skipLookup = () => {
    setParty(null);
    setAttendees([]);
    setStep("form");
  };

  const updateAttendee = (i, k, v) =>
    setAttendees((arr) => arr.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleEvent = (id) =>
    set("events", form.events.includes(id) ? form.events.filter((e) => e !== id) : [...form.events, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error(r.toastValidation);
      return;
    }
    const going = attendees.filter((a) => a.attending && a.name.trim());
    if (party && form.attending === "yes" && going.length === 0) {
      toast.error(r.noAttendee);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, guest_count: Number(form.guest_count) || 1 };
      if (party) {
        payload.party_id = party.id;
        payload.party_name = party.party_name;
        payload.attendees = form.attending === "yes" ? going.map(({ name, meal }) => ({ name, meal: meal || null })) : [];
        payload.guest_count = form.attending === "yes" ? going.length : 0;
        payload.meal = null;
      }
      await axios.post(`${API}/rsvp`, payload);
      setDone(true);
      toast.success(r.toastSuccess);
    } catch {
      toast.error(r.toastError);
    } finally {
      setSubmitting(false);
    }
  };

  const attending = form.attending === "yes";
  const showLookup = hasGuests && step === "lookup" && !done;

  return (
    <section id="rsvp" className="py-28 sm:py-40 px-6 bg-[#2C3552] text-[#FAFAF1]" data-testid="rsvp-section">
      <div className="max-w-3xl mx-auto">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#F4B8C6] mb-6">{r.overline}</p>
          <h2 className="font-serif-display font-light text-4xl sm:text-5xl leading-tight">
            {r.title} <span className="italic text-[#F4B8C6]">{r.titleAccent}</span>
          </h2>
          <p className="mt-6 text-base font-light text-[#FAFAF1]/60">
            {r.deadlinePre} <span className="text-[#F4B8C6]">{r.deadlineDate}</span>.
          </p>
        </Reveal>

        {done ? (
          <Reveal className="mt-16">
            <div className="border border-[#F4B8C6]/40 p-10 sm:p-14 text-center" data-testid="rsvp-success">
              <p className="font-serif-display italic text-3xl sm:text-4xl font-light text-[#F4B8C6]">{r.successTitle}</p>
              <p className="mt-4 text-base font-light text-[#FAFAF1]/70">
                {attending ? r.successYes : r.successNo}
              </p>
            </div>
          </Reveal>
        ) : showLookup ? (
          <Reveal className="mt-16" delay={0.1}>
            <div data-testid="rsvp-lookup">
              <Label className="text-[10px] uppercase tracking-[0.25em] text-[#FAFAF1]/50">{r.findTitle}</Label>
              <form onSubmit={search} className="flex flex-col sm:flex-row gap-4 mt-3">
                <Input
                  data-testid="rsvp-lookup-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={r.findPh}
                  className="editorial-input flex-1 text-[#FAFAF1] placeholder:text-[#FAFAF1]/25 border-[#FAFAF1]/25"
                />
                <button
                  data-testid="rsvp-lookup-btn"
                  type="submit"
                  disabled={searching}
                  className="text-[11px] uppercase tracking-[0.25em] px-8 py-3 rounded-full bg-[#F4B8C6] text-[#2C3552] hover:bg-[#FAFAF1] transition-colors duration-300 disabled:opacity-50"
                >
                  {searching ? "…" : r.findBtn}
                </button>
              </form>
              {results && results.length > 0 && (
                <div className="mt-8 space-y-3">
                  {results.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`rsvp-party-result-${p.id}`}
                      onClick={() => pickParty(p)}
                      className="w-full text-left border border-[#FAFAF1]/20 hover:border-[#F4B8C6] p-5 transition-colors duration-300"
                    >
                      <p className="font-serif-display text-2xl">{p.party_name}</p>
                      <p className="text-sm font-light text-[#FAFAF1]/60 mt-1">
                        {p.members.join(" · ")} — {p.seats} {r.seats}
                      </p>
                    </button>
                  ))}
                </div>
              )}
              {results && results.length === 0 && (
                <div className="mt-8" data-testid="rsvp-lookup-notfound">
                  <p className="font-light text-[#F4B8C6]">{r.notFound}</p>
                  <button
                    data-testid="rsvp-lookup-skip"
                    onClick={skipLookup}
                    className="mt-4 text-[11px] uppercase tracking-[0.25em] text-[#FAFAF1]/70 border-b border-[#FAFAF1]/40 pb-1 hover:text-[#F4B8C6] hover:border-[#F4B8C6] transition-colors duration-300"
                  >
                    {r.generalLink}
                  </button>
                </div>
              )}
            </div>
          </Reveal>
        ) : (
          <Reveal className="mt-16" delay={0.1}>
            <form onSubmit={submit} className="space-y-10" data-testid="rsvp-form">
              {party && (
                <div className="border border-[#F4B8C6]/40 p-5 flex items-center justify-between gap-4" data-testid="rsvp-party-banner">
                  <div>
                    <p className="font-serif-display text-2xl text-[#F4B8C6]">{party.party_name}</p>
                    <p className="text-xs font-light text-[#FAFAF1]/60 mt-1">
                      {party.seats} {r.seats}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="rsvp-party-change"
                    onClick={() => {
                      setStep("lookup");
                      setParty(null);
                      setResults(null);
                      setQ("");
                    }}
                    className="text-[10px] uppercase tracking-[0.2em] text-[#FAFAF1]/50 hover:text-[#F4B8C6] transition-colors duration-300"
                  >
                    {r.changeParty}
                  </button>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.25em] text-[#FAFAF1]/50">{r.name}</Label>
                  <Input
                    data-testid="rsvp-name-input"
                    value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    placeholder={r.namePh}
                    className="editorial-input mt-2 text-[#FAFAF1] placeholder:text-[#FAFAF1]/25 border-[#FAFAF1]/25"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.25em] text-[#FAFAF1]/50">{r.email}</Label>
                  <Input
                    data-testid="rsvp-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@email.com"
                    className="editorial-input mt-2 text-[#FAFAF1] placeholder:text-[#FAFAF1]/25 border-[#FAFAF1]/25"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-[0.25em] text-[#FAFAF1]/50">{r.join}</Label>
                <RadioGroup
                  value={form.attending}
                  onValueChange={(v) => set("attending", v)}
                  className="flex gap-8 mt-4"
                >
                  <label className="flex items-center gap-3 cursor-pointer">
                    <RadioGroupItem data-testid="rsvp-attending-yes" value="yes" className="border-[#F4B8C6] text-[#F4B8C6]" />
                    <span className="font-serif-display italic text-xl">{r.accept}</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <RadioGroupItem data-testid="rsvp-attending-no" value="no" className="border-[#F4B8C6] text-[#F4B8C6]" />
                    <span className="font-serif-display italic text-xl">{r.decline}</span>
                  </label>
                </RadioGroup>
              </div>

              {attending && (
                <>
                  {party ? (
                    <div data-testid="rsvp-attendees">
                      <Label className="text-[10px] uppercase tracking-[0.25em] text-[#FAFAF1]/50">
                        {r.whoAttending}
                      </Label>
                      <div className="mt-4 space-y-4">
                        {attendees.map((a, i) => (
                          <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-3 border-b border-[#FAFAF1]/10 pb-4">
                            <label className="flex items-center gap-3 flex-1 cursor-pointer">
                              <Checkbox
                                data-testid={`rsvp-attendee-check-${i}`}
                                checked={a.attending}
                                onCheckedChange={(v) => updateAttendee(i, "attending", !!v)}
                                className="border-[#F4B8C6] data-[state=checked]:bg-[#F4B8C6] data-[state=checked]:text-[#2C3552]"
                              />
                              {a.editable ? (
                                <Input
                                  data-testid={`rsvp-attendee-name-${i}`}
                                  value={a.name}
                                  onChange={(e) => updateAttendee(i, "name", e.target.value)}
                                  placeholder={r.guestNamePh}
                                  className="editorial-input font-serif-display text-xl text-[#FAFAF1] placeholder:text-[#FAFAF1]/25 border-[#FAFAF1]/25"
                                />
                              ) : (
                                <span className="font-serif-display text-xl">{a.name}</span>
                              )}
                            </label>
                            {a.attending && (
                              <div className="flex gap-2 pl-8 sm:pl-0">
                                {r.meals.map(([v, l]) => (
                                  <button
                                    type="button"
                                    key={v}
                                    data-testid={`rsvp-attendee-${i}-meal-${v}`}
                                    onClick={() => updateAttendee(i, "meal", v)}
                                    className={`text-[10px] uppercase tracking-[0.15em] px-3.5 py-1.5 rounded-full border transition-colors duration-300 ${
                                      a.meal === v
                                        ? "bg-[#F4B8C6] text-[#2C3552] border-[#F4B8C6]"
                                        : "border-[#FAFAF1]/30 text-[#FAFAF1]/70 hover:border-[#F4B8C6]"
                                    }`}
                                  >
                                    {l}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {attendees.length < party.seats && (
                        <button
                          type="button"
                          data-testid="rsvp-add-guest-btn"
                          onClick={addGuestRow}
                          className="mt-5 text-[11px] uppercase tracking-[0.25em] text-[#F4B8C6] border-b border-[#F4B8C6]/40 pb-1 hover:border-[#F4B8C6] transition-colors duration-300"
                        >
                          + {r.addGuest}
                        </button>
                      )}
                    </div>
                  ) : (
                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.25em] text-[#FAFAF1]/50">
                        {r.guests}
                      </Label>
                      <Input
                        data-testid="rsvp-guests-input"
                        type="number"
                        min="1"
                        max="10"
                        value={form.guest_count}
                        onChange={(e) => set("guest_count", e.target.value)}
                        className="editorial-input mt-2 text-[#FAFAF1] border-[#FAFAF1]/25"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.25em] text-[#FAFAF1]/50">{r.meal}</Label>
                      <RadioGroup value={form.meal} onValueChange={(v) => set("meal", v)} className="flex flex-wrap gap-6 mt-4">
                        {r.meals.map(([v, l]) => (
                          <label key={v} className="flex items-center gap-2 cursor-pointer">
                            <RadioGroupItem data-testid={`rsvp-meal-${v}`} value={v} className="border-[#F4B8C6] text-[#F4B8C6]" />
                            <span className="text-sm font-light">{l}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  </div>
                  )}

                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.25em] text-[#FAFAF1]/50">
                      {r.events}
                    </Label>
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mt-4">
                      {r.eventOptions.map(([id, label]) => (
                        <label key={id} className="flex items-center gap-3 cursor-pointer">
                          <Checkbox
                            data-testid={`rsvp-event-${id}`}
                            checked={form.events.includes(id)}
                            onCheckedChange={() => toggleEvent(id)}
                            className="border-[#F4B8C6] data-[state=checked]:bg-[#F4B8C6] data-[state=checked]:text-[#2C3552]"
                          />
                          <span className="text-sm font-light">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.25em] text-[#FAFAF1]/50">
                        {r.dietary}
                      </Label>
                      <Input
                        data-testid="rsvp-dietary-input"
                        value={form.dietary}
                        onChange={(e) => set("dietary", e.target.value)}
                        placeholder={r.dietaryPh}
                        className="editorial-input mt-2 text-[#FAFAF1] placeholder:text-[#FAFAF1]/25 border-[#FAFAF1]/25"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.25em] text-[#FAFAF1]/50">{r.song}</Label>
                      <Input
                        data-testid="rsvp-song-input"
                        value={form.song_request}
                        onChange={(e) => set("song_request", e.target.value)}
                        placeholder={r.songPh}
                        className="editorial-input mt-2 text-[#FAFAF1] placeholder:text-[#FAFAF1]/25 border-[#FAFAF1]/25"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label className="text-[10px] uppercase tracking-[0.25em] text-[#FAFAF1]/50">{r.note}</Label>
                <Textarea
                  data-testid="rsvp-message-input"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder={r.notePh}
                  rows={3}
                  className="editorial-input mt-2 text-[#FAFAF1] placeholder:text-[#FAFAF1]/25 border-[#FAFAF1]/25 resize-none"
                />
              </div>

              <button
                data-testid="rsvp-submit-btn"
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto text-[11px] uppercase tracking-[0.3em] px-12 py-4 rounded-full bg-[#E15A70] text-[#FAFAF1] hover:bg-[#FAFAF1] hover:text-[#2C3552] hover:-translate-y-0.5 transition-[background-color,color,transform] duration-300 disabled:opacity-50"
              >
                {submitting ? r.sending : r.submit}
              </button>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};
